import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import SMCField from '../SMCField';
import {useSMCDisabledContext} from '../SMCDisabledContext';
import {hasValue} from '../../../utils/utils';

/**
 * 
 * @param {object} params
 * @param {boolean} params.disabled
 * @param {boolean} params.readOnly
 * @param {(string|number)} params.value
 * @param {function} params.onChange
 * @param {string} params.className
 * @param {string} params.label
 * @param {('start'|'end')} params.labelPosition
 * @param {(string|number)} params.labelWidth
 * @param {string} params.tooltip
 * @param {boolean} params.inline
 * @param {boolean} params.noAutoComplete
 * @param {object} params.style
 * @param {string} params.placeholder
 * @param {(string|number)} params.height
 * @param {*} params.innerRef
 * @param {(string|number)} params.width
 * @param {string} params.type
 * @param {boolean} params.allowClearAll
 * @param {function} params.onClearAll
 * @param {string} params.suffixIcon
 * @param {boolean} params.required
 * @param {boolean} params.ignoreDisabledContext
 */
function SMCFileInput({
    disabled,
    readOnly,
    value,
    inline,
    onChange,
    className,
    label,
    labelPosition,
    labelWidth,
    tooltip,
    style,
    noAutoComplete,
    placeholder,
    width,
    height,
    type="text",
    innerRef,
    allowClearAll,
    onClearAll,
    suffixIcon,
    required,
    ignoreDisabledContext,
    ...rest
}) {
    const classes = useStyles();
    const disabledContext = useSMCDisabledContext();
    
    const _disabled = useMemo(() => {
        if (disabledContext && !ignoreDisabledContext) {
            if (disabledContext.forceDisable) {
                return true;
            }
            if (disabledContext.forceEnable) {
                return false;
            }
        }
        if (hasValue(disabled)) {
            return disabled;
        }
        return undefined;
    }, [disabled, disabledContext, ignoreDisabledContext]);

    const inputElem = (
        <div className={classes.root}>
            <input
                {...rest}
                style={{ width: width, height: height, ...style }}
                type="file"
                onChange={e => onChange && onChange([...e.target.files])}
                disabled={_disabled || readOnly}
                readOnly={_disabled || readOnly}
                className={
                    clsx(
                        className,
                        _disabled && classes.disabled,
                        readOnly && classes.readOnly
                    )
                }
            />
        </div>
    );

    return (
        <SMCField
            inline={inline}
            label={label}
            labelPosition={labelPosition}
            labelWidth={labelWidth}
            tooltip={tooltip}
            required={required}
        >
            {inputElem}
        </SMCField>
    )
}

const useStyles = makeStyles({
    root: {
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center'
    },
    disabled: {
        color: '#545454',
        backgroundColor: '#EBEBE4',
        '&:focus': {
            border: '1px solid #d2d5d9'
        }
    },
    readOnly: {
        border: 'none',
        background: 'none',
        // color: '#8f8f8f'
    }
})

SMCFileInput.propTypes = {
    disabled: PropTypes.bool,
    readOnly: PropTypes.bool,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func,
    className: PropTypes.string,
    label: PropTypes.node,
    labelPosition: PropTypes.oneOf(['start', 'end']),
    labelWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    tooltip: PropTypes.node,
    inline: PropTypes.bool,
    noAutoComplete: PropTypes.bool,
    style: PropTypes.object,
    placeholder: PropTypes.string,
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.string,
    innerRef: PropTypes.any,
    allowClearAll: PropTypes.bool,
    onClearAll: PropTypes.func,
    suffixIcon: PropTypes.string,
    required: PropTypes.bool,
    ignoreDisabledContext: PropTypes.bool
}

// SMCFileInput.whyDidYouRender = true;

export default React.memo(SMCFileInput);